import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Home, TrendingUp, Users, ClipboardCheck, Settings, ShieldCheck, FileSpreadsheet, ArrowRight } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
export function PropertyManagement() {
  const features = [{
    icon: <Settings size={24} data-id="element-400" />,
    title: 'Property Operations',
    desc: 'Day-to-day management, maintenance scheduling, and facility upkeep.'
  }, {
    icon: <TrendingUp size={24} data-id="element-401" />,
    title: 'Revenue Optimization',
    desc: 'Dynamic pricing strategies and occupancy maximization.'
  }, {
    icon: <Users size={24} data-id="element-402" />,
    title: 'Staff Management',
    desc: 'Recruitment, training, and payroll for villa and hotel staff.'
  }, {
    icon: <Home size={24} data-id="element-403" />,
    title: 'Guest Experience',
    desc: 'Premium concierge services and 24/7 guest support.'
  }, {
    icon: <ClipboardCheck size={24} data-id="element-404" />,
    title: 'SOP Development',
    desc: 'Creating and implementing international standard operating procedures.'
  }, {
    icon: <ShieldCheck size={24} data-id="element-405" />,
    title: 'Compliance Monitoring',
    desc: 'Ensuring adherence to local hospitality regulations and safety standards.'
  }, {
    icon: <FileSpreadsheet size={24} data-id="element-406" />,
    title: 'Monthly Reporting',
    desc: 'Transparent financial and operational performance reports.'
  }, {
    icon: <CheckCircle size={24} data-id="element-407" />,
    title: 'Vendor Coordination',
    desc: 'Managing relationships with suppliers and contractors.'
  }];
  return <div className="bg-navy-900 min-h-screen" data-id="element-408">
      <PageHeader title="Property Management" subtitle="Maximizing Asset Value & Operational Performance for luxury villas and hospitality businesses in Bali." backgroundImage="https://images.unsplash.com/photo-1613490900233-141c5560d75d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1974&q=80" data-id="element-409" />

      <section className="py-24" data-id="element-410">
        <div className="max-w-content mx-auto px-6 md:px-12" data-id="element-411">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24" data-id="element-412">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-413">
              <h2 className="font-display text-4xl font-bold text-white mb-6" data-id="element-414">
                Elevating Hospitality Standards
              </h2>
              <p className="font-body text-gray-400 text-lg leading-relaxed mb-6" data-id="element-415">
                At Marvesta Corp Bali, we understand that a luxury property is a
                significant investment. Our property management services are
                designed to protect that investment while maximizing its revenue
                potential.
              </p>
              <p className="font-body text-gray-400 text-lg leading-relaxed mb-8" data-id="element-416">
                We bring international corporate rigor to Bali's hospitality
                sector, combining meticulous operational oversight with
                transparent financial reporting. Whether you own a single luxury
                villa or a boutique hotel, our team acts as your dedicated owner
                representative on the ground.
              </p>
              <Button to="/contact" data-id="element-417">Discuss Your Property</Button>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="relative h-[500px] rounded-card overflow-hidden shadow-card" data-id="element-418">
              <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=85" alt="Luxury Bali villa managed for operational performance" loading="lazy" className="w-full h-full object-cover" data-id="element-419" />
            </motion.div>
          </div>

          <div className="text-center mb-16" data-id="element-420">
            <h2 className="font-display text-4xl font-bold text-white mb-4" data-id="element-421">
              Comprehensive Management Solutions
            </h2>
            <p className="font-body text-gray-400 text-lg max-w-2xl mx-auto" data-id="element-422">
              End-to-end services ensuring your property operates flawlessly and
              profitably.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-423">
            {features.map((feature, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.5
          }} className="bg-navy-800 p-8 rounded-card border border-navy-700 hover:border-gold/50 transition-colors" data-id="element-424">
                <div className="text-gold mb-6" data-id="element-425">{feature.icon}</div>
                <h3 className="font-heading text-xl font-semibold text-white mb-3" data-id="element-426">
                  {feature.title}
                </h3>
                <p className="font-body text-sm text-gray-400 leading-relaxed" data-id="element-427">
                  {feature.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Workflow Diagram Section */}
      <section className="py-24 bg-navy-800 border-y border-navy-700" data-id="element-428">
        <div className="max-w-content mx-auto px-6 md:px-12" data-id="element-429">
          <div className="text-center mb-16" data-id="element-430">
            <h2 className="font-display text-4xl font-bold text-white mb-4" data-id="element-431">
              Our Management Process
            </h2>
            <p className="font-body text-gray-400 text-lg max-w-2xl mx-auto" data-id="element-432">
              A systematic approach to onboarding and optimizing your property.
            </p>
          </div>

          <div className="relative" data-id="element-433">
            {/* Connecting Line */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-navy-700 -translate-y-1/2 z-0" data-id="element-434"></div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative z-10" data-id="element-435">
              {[{
              step: '01',
              title: 'Assessment',
              desc: 'Comprehensive property audit and financial review.'
            }, {
              step: '02',
              title: 'Strategy',
              desc: 'Developing SOPs, budget, and pricing models.'
            }, {
              step: '03',
              title: 'Implementation',
              desc: 'Staff training, marketing setup, and operational launch.'
            }, {
              step: '04',
              title: 'Optimization',
              desc: 'Continuous monitoring, reporting, and revenue growth.'
            }].map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.2,
              duration: 0.5
            }} className="bg-navy-900 p-8 rounded-card border border-navy-700 text-center relative" data-id="element-436">
                  <div className="w-12 h-12 bg-gold text-navy-900 rounded-full flex items-center justify-center font-display font-bold text-xl mx-auto mb-6 shadow-gold" data-id="element-437">
                    {item.step}
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-white mb-3" data-id="element-438">
                    {item.title}
                  </h3>
                  <p className="font-body text-sm text-gray-400" data-id="element-439">{item.desc}</p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 text-center" data-id="element-440">
        <div className="max-w-3xl mx-auto px-6 md:px-12" data-id="element-441">
          <h2 className="font-display text-4xl font-bold text-white mb-6" data-id="element-442">
            Ready to Optimize Your Property?
          </h2>
          <p className="font-body text-gray-400 text-lg mb-10" data-id="element-443">
            Contact us today for a comprehensive property assessment and
            management proposal.
          </p>
          <Button to="/contact" icon={<ArrowRight size={18} data-id="element-445" />} data-id="element-444">
            Request a Proposal
          </Button>
        </div>
      </section>
    </div>;
}